import { SxProps, Theme } from "@mui/material";
import formFieldMetrics from "./FormField.tokens";

const m = formFieldMetrics;

/**
 * Default chrome for every `CustomInput` in the app.
 *
 * Colours come from `theme.palette.formField.*`, geometry from
 * `FormField.tokens.ts` — see the note there on why these moved out of the
 * Contacts screen. A caller's `sx` is composed *on top* of these rather than
 * replacing them (see `CustomInput.tsx`), so a screen only has to state what it
 * genuinely does differently.
 */
const customInputStyles = {
  inputLabel: ((theme: Theme) => ({
    textAlign: "left",
    // `0`, not the old `5px`: the label lines up with the field's left edge,
    // and the field's own inset is what indents the text inside it.
    ml: 0,
    color: theme.palette.formField.label,
    fontSize: m.labelFontSize,
    fontWeight: m.labelFontWeight,
    lineHeight: "normal",
    "& .MuiFormLabel-asterisk": {
      color: theme.palette.formField.required,
      ml: m.asteriskGap,
    },
  })) as SxProps<Theme>,

  textField: ((theme: Theme) => ({
    "& .MuiOutlinedInput-root": {
      height: m.height,
      p: 0,
      borderRadius: m.radius,
      bgcolor: theme.palette.formField.bg,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.formField.border,
        borderWidth: m.borderWidth,
      },
      "&:hover": {
        bgcolor: theme.palette.formField.bg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.formField.border,
        },
      },
      "&.Mui-focused": {
        bgcolor: theme.palette.formField.bg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.formField.focusBorder,
          borderWidth: m.borderWidth,
        },
      },
      "&.Mui-disabled": {
        bgcolor: theme.palette.formField.disabledBg,
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.formField.disabledBorder,
        },
      },
      // The rules above are more specific than MUI's own error styling, so the
      // validation outline has to be restated or it never shows.
      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.error.main,
      },
      // A multiline field grows with its content, so the fixed height and the
      // input's own vertical centring both have to come off.
      "&.MuiInputBase-multiline": {
        height: "auto",
        p: `10px ${m.paddingX}`,
        "& .MuiOutlinedInput-input": { p: 0, height: "auto" },
      },
    },

    "& .MuiOutlinedInput-input": {
      height: "100%",
      boxSizing: "border-box",
      p: `0 ${m.paddingX}`,
      fontSize: m.fontSize,
      fontWeight: m.fontWeight,
      color: theme.palette.formField.text,
      "&::placeholder": {
        color: theme.palette.formField.placeholder,
        opacity: 1,
        fontSize: m.fontSize,
        fontWeight: m.fontWeight,
      },
      "&.Mui-disabled": {
        WebkitTextFillColor: theme.palette.formField.disabledText,
        color: theme.palette.formField.disabledText,
      },
    },

    // `select` fields render their value through `.MuiSelect-select`, which
    // MUI pads itself.
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      height: "100%",
      minHeight: "unset",
      boxSizing: "border-box",
      p: `0 ${m.paddingX} !important`,
      fontSize: m.fontSize,
      fontWeight: m.fontWeight,
      color: theme.palette.formField.text,
    },

    "& .MuiFormHelperText-root": {
      ml: 0,
      mt: m.helperGap,
      fontSize: m.helperFontSize,
    },

    // The `standard` variant is still used for a few inline fields; it draws
    // its own rule, so suppress MUI's underline as before.
    "& .MuiInput-underline:before": { borderBottom: "none !important" },
    "& .MuiInput-underline:hover:before": { borderBottom: "none !important" },
    "& .MuiInput-underline:after": { borderBottom: "none !important" },
  })) as SxProps<Theme>,
} as const;

export default customInputStyles;
